import { Check, ChevronsUpDown } from "lucide-react";
import { type ReactNode, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandCollection,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ComboboxProps<T> {
  items: T[];
  value: T | null;
  onChange: (item: T | null) => void;
  getKey: (item: T) => string | number;
  getLabel: (item: T) => string;
  /** Custom row content (icons, avatars…) rendered after the Check icon. */
  renderItem?: (item: T) => ReactNode;
  /** Custom content of the closed trigger for the selected item (an icon before its label…). */
  renderValue?: (item: T) => ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  /** Nom accessible du déclencheur, quand le champ n'a pas de `<Label>` à lui. */
  ariaLabel?: string;
  /** When set, prepends a "none" row that clears the selection. */
  unselectLabel?: string;
  /** Extra text matched by the search filter, in addition to the label. */
  getSearchValue?: (item: T) => string;
  /** Pinned action row at the bottom of the list (e.g. "Ajouter…"). */
  footerAction?: { label: ReactNode; onSelect: () => void };
  /** Max items displayed at once (search filters before the cap). */
  limit?: number;
  className?: string;
  /** Notified on open/close — lets callers gate an expensive `items` fetch on open state. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Server-side search: notified with the typed text, and local filtering is turned off —
   * `items` are expected to already match it.
   */
  onSearchChange?: (search: string) => void;
}

/**
 * Generic single-select combobox in the canonical shadcn anatomy: outline
 * Button trigger + ChevronsUpDown, popover with a searchable coss Command
 * list whose rows carry a leading Check icon (visible when selected).
 * Clicking the selected row (or the optional "none" row) clears the value.
 */
export function Combobox<T>({
  items,
  value,
  onChange,
  getKey,
  getLabel,
  renderItem,
  renderValue,
  placeholder = "Sélectionner…",
  searchPlaceholder = "Rechercher...",
  emptyMessage = "Aucun résultat",
  disabled = false,
  ariaLabel,
  unselectLabel,
  getSearchValue,
  footerAction,
  limit = 10,
  className,
  onOpenChange,
  onSearchChange,
}: ComboboxProps<T>) {
  const [open, setOpen] = useState(false);

  function updateOpen(next: boolean) {
    setOpen(next);
    onOpenChange?.(next);
  }

  const listItems: Array<T | null> = unselectLabel ? [null, ...items] : items;

  function isSelected(item: T) {
    return value !== null && getKey(item) === getKey(value);
  }

  function select(item: T | null) {
    if (item === null || isSelected(item)) {
      onChange(null);
    } else {
      onChange(item);
    }
    updateOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={updateOpen}>
      <PopoverTrigger
        disabled={disabled}
        render={
          <Button
            variant="outline"
            role="combobox"
            aria-label={ariaLabel}
            aria-expanded={open}
            className={cn(
              "w-full justify-between font-normal",
              value === null && "text-muted-foreground",
              className,
            )}
          />
        }
      >
        {value !== null && renderValue ? (
          <span className="flex min-w-0 items-center gap-2">{renderValue(value)}</span>
        ) : (
          <span className="truncate">{value === null ? placeholder : getLabel(value)}</span>
        )}
        <ChevronsUpDown
          className="ml-2 size-4 shrink-0 text-muted-foreground opacity-50"
          aria-hidden="true"
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--anchor-width) p-0">
        <Command
          items={listItems}
          limit={limit}
          {...(onSearchChange
            ? {
                filter: null,
                onValueChange: (search: string, details: { reason: string }) => {
                  // Picking a row writes its label into the input — that is not a search.
                  if (details.reason !== "item-press") {
                    onSearchChange(search);
                  }
                },
              }
            : {})}
          itemToStringValue={(item) => {
            const typed = item as T | null;
            if (typed === null) {
              return unselectLabel ?? "";
            }
            return getSearchValue ? `${getLabel(typed)} ${getSearchValue(typed)}` : getLabel(typed);
          }}
        >
          <CommandInput placeholder={searchPlaceholder} size="default" autoFocus={false} />
          <CommandEmpty>{emptyMessage}</CommandEmpty>
          <CommandList className="max-h-72 overflow-y-auto not-empty:p-1">
            <CommandCollection>
              {(item: T | null) => (
                <CommandItem
                  key={item === null ? "__none__" : getKey(item)}
                  value={item}
                  onClick={() => select(item)}
                >
                  <Check
                    className={cn(
                      "size-4 shrink-0",
                      (item === null ? value === null : isSelected(item)) ? "opacity-100" : "opacity-0",
                    )}
                    aria-hidden="true"
                  />
                  {item === null ? (
                    <span className="truncate">{unselectLabel}</span>
                  ) : (
                    (renderItem?.(item) ?? <span className="truncate">{getLabel(item)}</span>)
                  )}
                </CommandItem>
              )}
            </CommandCollection>
          </CommandList>
          {footerAction ? (
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 border-t px-3 py-2 text-sm text-foreground hover:bg-accent"
              onClick={() => {
                updateOpen(false);
                footerAction.onSelect();
              }}
            >
              {footerAction.label}
            </button>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
